import React, { useMemo, memo } from 'react';
import styled from 'styled-components';

import TicketStatus from '../TicketStatus';
import { STATUSES, PRIORITIES_BY_STATUS } from '../TicketStatus/constants';
import Select from '../../../Select';

import { TICKET_TYPES } from '../../../../constants';
import { TICKET_STATUS_PLACEMENT } from '../TicketStatus/constants';
import { ValueOf } from '@utils/tsUtils';
import { CloseReasonType, PlanReasonType } from '../types';

const { StyledSelected } = Select;

interface ITicketStatusSelector {
  className?: string;
  value?: ValueOf<typeof STATUSES> | null;
  time?: number | null;
  orderIds?: string[] | null;
  planReason: PlanReasonType | null;
  closeReason: CloseReasonType | null;
  priority: string;
  type: ValueOf<typeof TICKET_TYPES>;
  onClickStatus: (value: ValueOf<typeof STATUSES>) => void;
  onChange: (event: CustomEvent) => void;
}

const TicketStatusSelector = ({
  value = null,
  priority: ticketPriority,
  type = TICKET_TYPES.UNKNOWN,
  time = null,
  closeReason = null,
  planReason = null,
  onClickStatus,
  orderIds = null,
  onChange,
  ...props
}: ITicketStatusSelector) => {
  const statuses: Array<ValueOf<typeof STATUSES>> = [
    ...Object.values(STATUSES),
  ];
  const initialValue =
    !!value && statuses.includes(value) ? value : STATUSES.OPEN;
  const options = useMemo(
    () =>
      statuses.map(optionValue => ({
        value: optionValue,
        title: optionValue,
        priority: PRIORITIES_BY_STATUS[optionValue],
      })),
    [STATUSES]
  );
  const headRenderer = ({ title, priority }) => (
    <TicketStatus
      title={title}
      priority={priority}
      time={time}
      planReason={planReason}
      type={type}
      closeReason={closeReason}
      orderIds={orderIds}
      placement={TICKET_STATUS_PLACEMENT.TICKET_STATUS_SELECTOR}
    />
  );

  const optionRenderer = ({ value: optionValue, title, priority }) => (
    <TicketStatus
      title={title}
      onClick={() => onClickStatus(optionValue)}
      priority={priority}
      isOption
      placement={TICKET_STATUS_PLACEMENT.TICKET_STATUS_SELECTOR}
    />
  );

  return (
    <StyledStatusContainer>
      <StyledTicketStatusSelector
        {...props}
        onChange={onChange}
        value={initialValue}
        options={options}
        headRenderer={headRenderer}
        optionRenderer={optionRenderer}
      />
    </StyledStatusContainer>
  );
};

TicketStatusSelector.displayName = 'TicketStatusSelector';

TicketStatusSelector.defaultProps = {
  className: '',
  value: null,
  time: null,
  orderIds: null,
  closeReason: null,
  planReason: null,
  type: TICKET_TYPES.UNKNOWN,
};

const StyledStatusContainer = styled.div`
  width: auto;
`;

const StyledTicketStatusSelector = styled(Select)`
  height: 30px;
  border: none;
  width: 200px;
  line-height: 30px;
  background: transparent;

  * {
    outline: none;
  }

  ${StyledSelected} {
    padding: 0;
  }
`;

export default memo(TicketStatusSelector);
